/**
 * Who is signed in to the admin panel, and the one helper every admin API call
 * goes through.
 *
 * Sign-in is a setting on the admin service. When it is off, nothing here gets
 * in the way: no token is kept, no header is sent, and every page works as it
 * always has. When it is on, the token from POST /auth/login is kept in
 * localStorage and sent as `Authorization: Bearer <token>` on every call.
 */

import { getAdminApiBaseUrl } from "@/lib/api/config";

export const TOKEN_KEY = "omnium.admin.token";

export type Account = {
  id: string;
  email: string;
  name: string;
  role: string;
  canWrite: boolean;
};

export type AuthState =
  /** GET /auth/me has not answered yet. */
  | { status: "checking" }
  | { status: "ready"; required: boolean; account: Account | null }
  /** The admin service did not answer. The panel opens and each page shows its own error. */
  | { status: "unreachable" };

/** `logged_out`: the person pressed Sign out. `expired`: omnium said the sign-in is no good. */
export type SignOutReason = "logged_out" | "expired";

// --------------------------------------------------------------------------- token

/** Used when the browser has no localStorage (tests, private modes that block it). */
let memoryToken: string | null = null;

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  const store = storage();
  if (!store) return memoryToken;
  try {
    return store.getItem(TOKEN_KEY) || null;
  } catch {
    return memoryToken;
  }
}

export function setToken(token: string | null): void {
  memoryToken = token;
  const store = storage();
  if (!store) return;
  try {
    if (token) store.setItem(TOKEN_KEY, token);
    else store.removeItem(TOKEN_KEY);
  } catch {
    // Kept in memory for this tab only.
  }
}

/** Add the sign-in header to a request, when there is a token. */
export function withAuth(init: RequestInit = {}): RequestInit {
  const token = getToken();
  if (!token) return init;
  const headers = new Headers(init.headers);
  if (!headers.has("authorization")) headers.set("authorization", `Bearer ${token}`);
  return { ...init, headers };
}

function isAuthRoute(url: string): boolean {
  try {
    return new URL(url, "http://omnium.invalid").pathname.startsWith("/auth/");
  } catch {
    return false;
  }
}

/**
 * Every admin API call. Adds the sign-in, never caches, and notices when
 * omnium says the sign-in is no good (401) so the panel can ask again.
 */
export async function apiFetch(
  url: string,
  init: RequestInit = {},
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  const response = await fetchImpl(url, withAuth({ cache: "no-store", ...init }));
  if (response.status === 401 && !isAuthRoute(url)) expired();
  return response;
}

// --------------------------------------------------------------------------- state

let state: AuthState = { status: "checking" };
const stateListeners = new Set<() => void>();
const signOutListeners = new Set<(reason: SignOutReason) => void>();

export function getAuth(): AuthState {
  return state;
}

function setAuth(next: AuthState): void {
  state = next;
  for (const listener of stateListeners) listener();
}

function emitSignedOut(reason: SignOutReason): void {
  for (const listener of signOutListeners) listener(reason);
}

let watchingOtherTabs = false;

/** Signing out in one tab signs out every tab. */
function watchOtherTabs(): void {
  if (watchingOtherTabs || typeof window === "undefined") return;
  watchingOtherTabs = true;
  window.addEventListener("storage", (event) => {
    if (event.key !== TOKEN_KEY) return;
    if (event.oldValue && !event.newValue) {
      memoryToken = null;
      if (state.status === "ready") setAuth({ ...state, account: null });
      emitSignedOut("logged_out");
    }
  });
}

export function subscribeAuth(listener: () => void): () => void {
  watchOtherTabs();
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
}

/** Call back when the person is signed out, here or in another tab. */
export function onSignedOut(listener: (reason: SignOutReason) => void): () => void {
  watchOtherTabs();
  signOutListeners.add(listener);
  return () => signOutListeners.delete(listener);
}

function expired(): void {
  const hadToken = getToken() !== null;
  setToken(null);
  // A 401 means sign-in is on, even if /auth/me said otherwise a while ago.
  setAuth({ status: "ready", required: true, account: null });
  if (hadToken) emitSignedOut("expired");
}

/** "that email and password do not match" → "That email and password do not match." */
export function sentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  const first = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(first) ? first : `${first}.`;
}

function detailOf(body: unknown): string | null {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "message" in detail) {
    return String((detail as { message: unknown }).message);
  }
  return null;
}

// --------------------------------------------------------------------------- calls

/** Ask omnium whether a sign-in is needed, and whose token this is. */
export async function checkSignIn(
  fetchImpl: typeof fetch = fetch,
  timeoutMs = 5_000,
): Promise<AuthState> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(
      `${getAdminApiBaseUrl()}/auth/me`,
      withAuth({ cache: "no-store", signal: controller.signal }),
    );
    if (!response.ok) throw new Error(`omnium answered ${response.status}`);
    const me = (await response.json()) as { required: boolean; account: Account | null };
    // A token nobody owns any more is thrown away.
    if (!me.account && getToken()) setToken(null);
    setAuth({ status: "ready", required: Boolean(me.required), account: me.account ?? null });
  } catch {
    setAuth({ status: "unreachable" });
  } finally {
    clearTimeout(timer);
  }
  return state;
}

export class SignInError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SignInError";
  }
}

export async function signIn(
  email: string,
  password: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Account> {
  let response: Response;
  try {
    response = await fetchImpl(`${getAdminApiBaseUrl()}/auth/login`, {
      method: "POST",
      cache: "no-store",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    throw new SignInError("omnium is not answering. Check that the admin service is running.");
  }
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new SignInError(sentence(detailOf(body) ?? `Sign-in failed (${response.status})`));
  }
  const { token, account } = body as { token: string; account: Account };
  setToken(token);
  setAuth({
    status: "ready",
    required: state.status === "ready" ? state.required : false,
    account,
  });
  return account;
}

/** End the sign-in here and on the server. Framed apps are told their session is over. */
export async function signOut(fetchImpl: typeof fetch = fetch): Promise<void> {
  if (getToken()) {
    try {
      await fetchImpl(`${getAdminApiBaseUrl()}/auth/logout`, withAuth({ method: "POST" }));
    } catch {
      // The token is dropped here either way; it runs out on its own.
    }
  }
  setToken(null);
  if (state.status === "ready") setAuth({ ...state, account: null });
  emitSignedOut("logged_out");
}

// --------------------------------------------------------------------------- the gate

export type Gate = "wait" | "sign_in" | "open";

/** Wait for the answer; send to /login only when omnium needs a sign-in and has none. */
export function gateFor(auth: AuthState): Gate {
  if (auth.status === "checking") return "wait";
  if (auth.status === "ready" && auth.required && !auth.account) return "sign_in";
  return "open";
}

/** Where to go after signing in. Only a path inside the panel, and never back to /login. */
export function safeNext(next: string | null | undefined): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/login")) {
    return "/dashboard";
  }
  return next;
}
